import React from "react";
import { Button } from "../ui/button";
import { Calendar, MessageCircle, Star, Heart, MapPin, Instagram, Sparkles } from "lucide-react";
import { SITE, waLink } from "../../lib/site";
import Reveal from "../Reveal";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-24 sm:pt-36 sm:pb-32 overflow-hidden text-white bg-transparent"
    >
      <div className="container-pad relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <Reveal as="div" className="inline-flex items-center gap-2 bg-white/12 backdrop-blur-xl border border-white/25 rounded-full px-4 py-2 mb-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
              <span className="flex h-2 w-2 rounded-full bg-gold animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white">
                Banho • Tosa • Higienização
              </span>
            </Reveal>

            <Reveal as="h1" delay={80} className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.02] tracking-tight text-balance drop-shadow-[0_18px_40px_rgba(0,0,0,0.28)]">
              Cuidado premium para o seu pet ficar
              <span className="block italic text-gold">limpo, cheiroso e feliz.</span>
            </Reveal>

            <Reveal as="p" delay={160} className="mt-6 text-base sm:text-xl text-white/82 max-w-xl leading-relaxed">
              Banho, tosa e higienização com carinho, segurança e atenção em cada detalhe.
            </Reveal>

            <Reveal as="div" delay={240} className="mt-9 flex flex-wrap gap-3">
              <Button
                asChild
                className="rounded-full bg-gold hover:bg-gold-hover text-royal font-bold px-7 py-6 text-base shadow-2xl shadow-gold/30 hover:shadow-gold/50 hover:-translate-y-1 transition-all"
              >
                <a href="#agendamento">
                  <Calendar className="w-5 h-5 mr-2" strokeWidth={2.4} />
                  Agendar Atendimento
                </a>
              </Button>

              <Button
                asChild
                className="rounded-full bg-white/14 hover:bg-white/22 text-white border border-white/25 backdrop-blur-xl font-semibold px-7 py-6 text-base shadow-[0_20px_60px_rgba(0,0,0,0.18)] hover:-translate-y-1 transition-all"
              >
                <a href={waLink()} target="_blank" rel="noreferrer">
                  <MessageCircle className="w-5 h-5 mr-2" strokeWidth={2.4} />
                  Falar no WhatsApp
                </a>
              </Button>
            </Reveal>

            <Reveal as="div" delay={320} className="mt-10 grid sm:grid-cols-2 gap-4 max-w-xl">
              <div className="rounded-3xl bg-white/12 border border-white/20 backdrop-blur-xl p-4 shadow-[0_20px_70px_rgba(0,0,0,0.18)] hover:-translate-y-1 transition-all">
                <div className="flex items-center gap-2 text-gold mb-1">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-bold">Atendimento com carinho</span>
                </div>
                <p className="text-sm text-white/72">Seu pet respeitado no tempo dele.</p>
              </div>

              <div className="rounded-3xl bg-white/12 border border-white/20 backdrop-blur-xl p-4 shadow-[0_20px_70px_rgba(0,0,0,0.18)] hover:-translate-y-1 transition-all">
                <div className="flex items-center gap-1 text-gold mb-1">
                  {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                    <Star key={k} className="w-4 h-4 fill-gold" />
                  ))}
                </div>
                <p className="text-sm text-white/72">Famílias felizes com pets bem cuidados.</p>
              </div>
            </Reveal>

            <Reveal as="div" delay={400} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/80">
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" />
                Ponta Aguda, Blumenau - SC
              </span>
              <a href={SITE.instagramUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                <Instagram className="w-4 h-4 text-gold" />
                {SITE.instagram}
              </a>
            </Reveal>
          </div>

          <Reveal as="div" delay={140} className="lg:col-span-6 relative">
            <div className="relative min-h-[520px] flex items-center justify-center">
              <div className="absolute inset-8 rounded-[48px] bg-white/10 border border-white/20 backdrop-blur-2xl shadow-[0_40px_120px_rgba(0,0,0,0.35)] rotate-3" />

              <img
                src="https://customer-assets.emergentagent.com/job_pawcare-premium-1/artifacts/c5zmoo5o_image.png"
                alt="Tia Sandra com um cachorro branco"
                className="relative z-10 w-full h-[500px] sm:h-[620px] object-contain drop-shadow-[0_50px_60px_rgba(0,0,0,0.55)] hover:scale-[1.025] transition-transform duration-[1200ms]"
                style={{
                  WebkitMaskImage: "radial-gradient(ellipse 78% 82% at 50% 50%, #000 62%, transparent 100%)",
                  maskImage: "radial-gradient(ellipse 78% 82% at 50% 50%, #000 62%, transparent 100%)",
                }}
              />

              <div className="absolute bottom-5 left-4 sm:left-1/2 sm:-translate-x-1/2 z-20 bg-white/90 backdrop-blur-xl text-royal rounded-full pl-2 pr-5 py-2 shadow-2xl flex items-center gap-3 ring-1 ring-white/80">
                <span className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center text-white shrink-0">
                  <MessageCircle className="w-5 h-5" strokeWidth={2.2} />
                </span>
                <span className="font-heading text-base sm:text-lg font-semibold">{SITE.phone}</span>
              </div>

              <div className="absolute top-8 left-2 z-20 bg-gold text-royal font-bold rounded-full px-4 py-2 shadow-2xl text-sm rotate-[-6deg]">
                🐾 Feito com amor
              </div>

              <div className="absolute top-14 right-8 z-20 bg-white/15 backdrop-blur-md rounded-full p-3 ring-1 ring-white/30 animate-float-medium">
                <Heart className="w-5 h-5 text-gold fill-gold" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
